import type {
  DateRangeValue,
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import {
  FottServiceRequestStatus,
  FottServiceType,
  type FottServiceSearchRequestPayload,
} from "@services/terminal-services/types";
import { convertUaeToUtc, toDate } from "@utils/date-time";

const LANG_BASE = "terminal-services.fott-services";
const LANG_BASE_HEADER = `${LANG_BASE}.tableHeaders`;

export const FOTT_SERVICES_COLUMNS = Object.freeze({
  ID: "serviceId",
  TYPE: "serviceType",
  BOOKING_REQUESTED_FROM: "bookingRequestedFrom",
  BOOKING_REQUESTED_TO: "bookingRequestedTo",
  ON_BEHALF: "onBehalf",
  CHARGEABLE_PARTY: "chargeableParty",
  NO_OF_USAGE_HOURS: "numberOfUsageHours",
  STATUS: "status",
  ACTIONS: "actions",
});

export const buildFottServicesFilterFields = (
  t: (key: string, options?: { defaultValue?: string }) => string,
  companyOptions: { value: string; label: string }[],
): Record<string, FilterFieldDefinition> => {
  const serviceTypeOptions = Object.values(FottServiceType).map((value) => ({
    value,
    label: t(`${LANG_BASE}.service-type.${value}`),
  }));
  const statusOptions = Object.values(FottServiceRequestStatus).map((value) => ({
    value,
    label: t(`${LANG_BASE}.status.${value}`),
  }));

  return {
    serviceId: {
      label: t(`${LANG_BASE_HEADER}.serviceId`),
      type: "text",
      minLength: 1,
    },
    serviceType: {
      label: t(`${LANG_BASE_HEADER}.serviceType`),
      type: "autocomplete",
      options: serviceTypeOptions,
      matchFrom: "start",
      inputLengthForDropdown: 1,
    },
    bookingRequestedFrom: {
      label: t(`${LANG_BASE_HEADER}.bookingRequestedFrom`),
      type: "dateFromTo",
    },
    bookingRequestedTo: {
      label: t(`${LANG_BASE_HEADER}.bookingRequestedTo`),
      type: "dateFromTo",
    },
    onBehalfCompanyCode: {
      label: t(`${LANG_BASE_HEADER}.onBehalf`),
      type: "autocomplete",
      options: companyOptions,
      matchFrom: "start",
      inputLengthForDropdown: 1,
    },
    chargeablePartyCompanyCode: {
      label: t(`${LANG_BASE_HEADER}.chargeableParty`),
      type: "autocomplete",
      options: companyOptions,
      matchFrom: "start",
      inputLengthForDropdown: 1,
    },
    numberOfUsageHours: {
      label: t(`${LANG_BASE_HEADER}.noOfUsageHours`),
      type: "numberFromTo",
      maxLength: 3,
      maxDigits: 2,
    },
    status: {
      label: t(`${LANG_BASE_HEADER}.status`),
      type: "autocomplete",
      options: statusOptions,
      matchFrom: "start",
      inputLengthForDropdown: 1,
    },
  };
};

export const buildFottServicesFilterBody = (
  filters: FilterItem[],
): FottServiceSearchRequestPayload => {
  const body: FottServiceSearchRequestPayload = {};

  for (const filter of filters) {
    if (!filter.field || filter.value == null || filter.value === "") {
      continue;
    }

    switch (filter.field) {
      case "serviceId": {
        const parsed = Number(filter.value);

        if (!Number.isNaN(parsed)) {
          body.serviceId = [...(body.serviceId ?? []), parsed];
        }

        break;
      }

      case "serviceType": {
        const value = String(filter.value).trim() as FottServiceType;
        body.serviceType = [...(body.serviceType ?? []), value];
        break;
      }

      case "bookingRequestedFrom": {
        const range = filter.value as DateRangeValue;
        const fromDate = toDate(range.from);
        const toDateValue = toDate(range.to);

        if (fromDate || toDateValue) {
          body.bookingRequestedFrom = [
            ...(body.bookingRequestedFrom ?? []),
            {
              ...(fromDate ? { from: convertUaeToUtc(fromDate) } : {}),
              ...(toDateValue ? { to: convertUaeToUtc(toDateValue) } : {}),
            },
          ];
        }

        break;
      }

      case "bookingRequestedTo": {
        const range = filter.value as DateRangeValue;
        const fromDate = toDate(range.from);
        const toDateValue = toDate(range.to);

        if (fromDate || toDateValue) {
          body.bookingRequestedTo = [
            ...(body.bookingRequestedTo ?? []),
            {
              ...(fromDate ? { from: convertUaeToUtc(fromDate) } : {}),
              ...(toDateValue ? { to: convertUaeToUtc(toDateValue) } : {}),
            },
          ];
        }

        break;
      }

      case "onBehalfCompanyCode": {
        const value = String(filter.value).trim();
        body.onBehalfCompanyCode = [...(body.onBehalfCompanyCode ?? []), value];
        break;
      }

      case "chargeablePartyCompanyCode": {
        const value = String(filter.value).trim();
        body.chargeablePartyCompanyCode = [...(body.chargeablePartyCompanyCode ?? []), value];
        break;
      }

      case "numberOfUsageHours": {
        const range = filter.value as { from?: string; to?: string };

        const from = range.from ? Number(range.from) : null;
        const to = range.to ? Number(range.to) : null;

        const normalizedFrom = from !== null && !Number.isNaN(from) ? from : null;
        const normalizedTo = to !== null && !Number.isNaN(to) ? to : null;

        const includeNull =
          (normalizedFrom === null && normalizedTo !== null) ||
          (normalizedFrom === 0 && normalizedTo === 0);

        body.numberOfUsageHours = [
          ...(body.numberOfUsageHours ?? []),
          {
            from: normalizedFrom,
            to: normalizedTo,
            includeNull,
          },
        ];

        break;
      }

      case "status": {
        const value = String(filter.value).trim() as FottServiceRequestStatus;
        body.status = [...(body.status ?? []), value];
        break;
      }
      default:
        break;
    }
  }

  return body;
};
